import type {
  Filter,
  FilterId,
  ListFilter,
  TreeFilter,
} from '../../types/filters';

/**
 * Helpers for building up filter instances while traversing the AI expression.
 *
 * Important context:
 * - We store `Map<FilterId, FilterInstance[]>` rather than a single instance because
 *   multi-filters (MULTI_*) can represent multiple independent "chips" when the AI expression
 *   contains top-level AND conjuncts.
 */
export const dedupeTreeSelections = (
  selections: TreeFilter['selections']
): TreeFilter['selections'] => {
  const seen = new Set<string>();
  const result: TreeFilter['selections'] = [];

  for (const s of selections) {
    const key = `${s.levelId}:${s.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(s);
  }

  return result;
};

export const dedupeListSelections = (
  selections: ListFilter['selections']
): ListFilter['selections'] => {
  const seen = new Set<string>();
  const result: ListFilter['selections'] = [];

  for (const s of selections) {
    const key = s.id;
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(s);
  }

  return result;
};

export const upsertInstance = <T extends Filter>(
  map: Map<FilterId, T[]>,
  filterId: FilterId,
  match: (existing: T) => boolean,
  create: () => T,
  merge: (existing: T) => T
): void => {
  // Upsert semantics:
  // - if no instance matches, create a new one
  // - if a match exists, merge into it (commonly used to accumulate OR-ed selections)
  const existing = map.get(filterId) || [];
  const idx = existing.findIndex(match);
  if (idx === -1) {
    map.set(filterId, [...existing, create()]);
    return;
  }
  const current = existing[idx];
  if (!current) return;
  const updated = merge(current);
  const next = [...existing];
  next[idx] = updated;
  map.set(filterId, next);
};

export const appendInstance = <T extends Filter>(
  map: Map<FilterId, T[]>,
  filterId: FilterId,
  instance: T
): void => {
  // "Always add a new chip" semantics (used when we intentionally want multiple instances).
  map.set(filterId, [...(map.get(filterId) || []), instance]);
};

export const mergeInstancesByFilter = <T extends Filter>(
  target: Map<FilterId, T[]>,
  source: Map<FilterId, T[]>
): void => {
  // Merge the per-conjunct group results into the global accumulator.
  source.forEach((instances, filterId) => {
    if (instances.length === 0) return;
    target.set(filterId, [...(target.get(filterId) || []), ...instances]);
  });
};
